import React, { useState } from "react";
import { Card, CardBody, CardHeader, Col, Row, Label, Input, FormFeedback, Button, Badge } from "reactstrap";
import { toast } from "react-toastify";

const MAX_SIZE_MB = 10;

const PasoMedicamentos = ({ datos, actualizarDatos, onAnterior, onSiguiente }) => {
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(datos.capturaMedicamentos ? "loaded" : null);
  const [nombreMedicamento, setNombreMedicamento] = useState("");
  const [cantidadMedicamento, setCantidadMedicamento] = useState("");
  const [enRecipe, setEnRecipe] = useState(true);
  const [errorMedicamentos, setErrorMedicamentos] = useState("");
  const [editandoIndex, setEditandoIndex] = useState(null);

  const handleArchivo = (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;

    if (archivo.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`El archivo no debe superar ${MAX_SIZE_MB} MB`);
      return;
    }

    const tiposPermitidos = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!tiposPermitidos.includes(archivo.type)) {
      setError("Formato no permitido. Use JPG, PNG, WEBP o PDF.");
      return;
    }

    setError("");
    actualizarDatos({ capturaMedicamentos: archivo });
    setPreview(URL.createObjectURL(archivo));
  };

  const handleEliminar = () => {
    actualizarDatos({ capturaMedicamentos: null });
    setPreview(null);
  };

  const handleAgregarMedicamento = () => {
    const nombre = nombreMedicamento.trim();
    if (!nombre) {
      setErrorMedicamentos("Ingrese el nombre del medicamento");
      return;
    }

    const cantidad = cantidadMedicamento ? parseInt(cantidadMedicamento, 10) : null;
    if (cantidadMedicamento && (isNaN(cantidad) || cantidad < 1)) {
      setErrorMedicamentos("Ingrese una cantidad válida (mínimo 1) o deje vacío");
      return;
    }

    const medicamentos = datos.medicamentos || [];
    const nuevoMedicamento = { nombre, cantidad, enRecipe };
    
    actualizarDatos({ medicamentos: [...medicamentos, nuevoMedicamento] });
    setNombreMedicamento("");
    setCantidadMedicamento("");
    setEnRecipe(true);
    setErrorMedicamentos("");
    toast.success("Medicamento agregado");
  };

  const handleEliminarMedicamento = (index) => {
    const medicamentos = datos.medicamentos || [];
    const nuevaLista = medicamentos.filter((_, i) => i !== index);
    actualizarDatos({ medicamentos: nuevaLista });
    setEditandoIndex(null);
    toast.info("Medicamento eliminado");
  };

  const handleEditarCantidad = (index, nuevaCantidad) => {
    const medicamentos = datos.medicamentos || [];
    const cantidad = nuevaCantidad ? parseInt(nuevaCantidad, 10) : null;
    if (nuevaCantidad && (isNaN(cantidad) || cantidad < 1)) {
      toast.warning("La cantidad debe ser al menos 1 o deje vacío");
      return;
    }
    const nuevaLista = medicamentos.map((med, i) =>
      i === index ? { ...med, cantidad } : med
    );
    actualizarDatos({ medicamentos: nuevaLista });
    setEditandoIndex(null);
    toast.success("Cantidad actualizada");
  };

  const handleSiguiente = () => {
    const medicamentos = datos.medicamentos || [];
    
    if (medicamentos.length === 0) {
      setErrorMedicamentos("Debe agregar al menos un medicamento");
      toast.warning("Agregue al menos un medicamento para continuar.");
      return;
    }

    if (!datos.capturaMedicamentos) {
      setError("Debe cargar la captura de medicamentos de la página de Mercantil.");
      toast.warning("Cargue la captura de medicamentos para continuar.");
      return;
    }
    
    onSiguiente();
  };

  const archivoActual = datos.capturaMedicamentos;
  const esImagen = archivoActual instanceof File && archivoActual.type?.startsWith("image/");

  return (
    <Card>
      <CardHeader>
        <h5 className="mb-0">
          <i className="ri-capsule-line me-2 text-primary"></i>
          Paso 3 — Medicamentos
        </h5>
      </CardHeader>
      <CardBody>
        {/* Sección de Medicamentos */}
        <div className="mb-4">
          <h6 className="mb-3">
            <i className="ri-capsule-line me-2 text-primary"></i>
            Medicamentos de la Solicitud
          </h6>
          <div className="alert alert-info d-flex align-items-start gap-2 mb-3">
            <i className="ri-information-line fs-5 mt-1"></i>
            <div>
              <strong>Comparación de los medicamentos entre lo solicitado e indicado en el recipe</strong>
              <br />
              <small>Agregue cada medicamento e indique si está presente en el recipe médico.</small>
            </div>
          </div>

          {/* Formulario para agregar medicamentos */}
          <Row className="gy-2 mb-3">
            <Col md={5}>
              <Input
                type="text"
                placeholder="Nombre del medicamento (ej: Paracetamol 500mg)"
                value={nombreMedicamento}
                onChange={(e) => {
                  setNombreMedicamento(e.target.value);
                  setErrorMedicamentos("");
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAgregarMedicamento();
                  }
                }}
                invalid={!!errorMedicamentos}
              />
            </Col>
            <Col md={2}>
              <Input
                type="number"
                min="1"
                placeholder="Cant."
                value={cantidadMedicamento}
                onChange={(e) => {
                  setCantidadMedicamento(e.target.value);
                  setErrorMedicamentos("");
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAgregarMedicamento();
                  }
                }}
                invalid={!!errorMedicamentos}
              />
              {errorMedicamentos && <FormFeedback>{errorMedicamentos}</FormFeedback>}
            </Col>
            <Col md={3}>
              <div className="form-check form-switch d-flex align-items-center" style={{ height: "100%" }}>
                <Input
                  type="checkbox"
                  role="switch"
                  id="enRecipeSwitch"
                  checked={enRecipe}
                  onChange={(e) => setEnRecipe(e.target.checked)}
                  className="form-check-input me-2"
                  style={{ cursor: "pointer" }}
                />
                <Label className="form-check-label mb-0" htmlFor="enRecipeSwitch" style={{ cursor: "pointer" }}>
                  {enRecipe ? "✔️ En recipe" : "❌ No en recipe"}
                </Label>
              </div>
            </Col>
            <Col md={2}>
              <Button color="primary" className="w-100" onClick={handleAgregarMedicamento}>
                <i className="ri-add-line me-1"></i>
                Agregar
              </Button>
            </Col>
          </Row>

          {/* Lista de medicamentos agregados */}
          {datos.medicamentos && datos.medicamentos.length > 0 && (
            <div className="border rounded p-3 bg-light">
              <div className="d-flex flex-wrap gap-2">
                {datos.medicamentos.map((med, index) => (
                  <Badge
                    key={index}
                    color={med.enRecipe ? "success" : "danger"}
                    className="d-flex align-items-center gap-2 py-2 px-3"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <span>{med.enRecipe ? "✔️" : "❌"}</span>
                    <span>{med.nombre}</span>
                    {editandoIndex === index ? (
                      <Input
                        type="number"
                        min="1"
                        size="sm"
                        style={{ width: "60px", fontSize: "0.75rem" }}
                        defaultValue={med.cantidad}
                        onBlur={(e) => handleEditarCantidad(index, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleEditarCantidad(index, e.target.value);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="badge bg-light text-dark cursor-pointer"
                        style={{ fontSize: "0.75rem", cursor: "pointer" }}
                        onClick={() => setEditandoIndex(index)}
                        title="Click para editar cantidad"
                      >
                        Cant: {med.cantidad || "?"}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      style={{ fontSize: "0.65rem" }}
                      onClick={() => handleEliminarMedicamento(index)}
                      aria-label="Eliminar"
                    ></button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <hr className="my-4" />

        {/* Sección de Captura de Pantalla */}
        <div className="mb-4">
          <h6 className="mb-3">
            <i className="ri-image-line me-2 text-primary"></i>
            Captura de Pantalla
          </h6>
          <div className="alert alert-info d-flex align-items-start gap-2 mb-3">
            <i className="ri-information-line fs-5 mt-1"></i>
            <div>
              Cargue una <strong>captura de pantalla</strong> de la página de Seguros Mercantil
              donde se listan los medicamentos solicitados. Esta imagen será adjuntada como
              comentario de la solicitud.
            </div>
          </div>
        </div>

        <Row className="gy-3">
          <Col md={12}>
            <Label className="form-label fw-semibold">
              Captura de Medicamentos <span className="text-danger">*</span>
            </Label>
            <Input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              onChange={handleArchivo}
              className={error ? "is-invalid" : ""}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </Col>

          {preview && archivoActual && (
            <Col md={12}>
              <div className="border rounded p-3 bg-light position-relative">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger position-absolute"
                  style={{ top: 8, right: 8 }}
                  onClick={handleEliminar}
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>

                {esImagen ? (
                  <img
                    src={preview}
                    alt="Captura de medicamentos"
                    style={{ maxWidth: "100%", maxHeight: 360, objectFit: "contain" }}
                    className="rounded"
                  />
                ) : (
                  <div className="d-flex align-items-center gap-2 py-2">
                    <i className="ri-file-pdf-line fs-3 text-danger"></i>
                    <span>{archivoActual.name}</span>
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>

        <div className="d-flex justify-content-between mt-4">
          <button className="btn btn-light" onClick={onAnterior}>
            <i className="ri-arrow-left-line me-1"></i> Anterior
          </button>
          <button className="btn btn-primary" onClick={handleSiguiente}>
            Siguiente <i className="ri-arrow-right-line ms-1"></i>
          </button>
        </div>
      </CardBody>
    </Card>
  );
};

export default PasoMedicamentos;
